import Fuse from 'fuse.js';
import {navigate, outOfVerticalBounds, positionDropdown} from '../SearchableSelects/Dom';
import {CloseDropdownMenus} from '../../DropdownMenu/Event';
import {CloseDropdowns, SelectCleared, SelectOptionsChanged} from '../SearchableSelects/Event';
import {buildGroupedLabel} from './Label';
import {bindRemoteSearch} from './RemoteSearch';

const DROPDOWN = `
    <div class="dropdown">
        <div class="handle"></div>
        <div class="content">
            <div class="search">
                <i class="fa-solid fa-magnifying-glass"></i>
                <input type="text" tabindex="0" autocomplete="off">
            </div>
            <ul class="options" tabindex="-1"></ul>
        </div>
    </div>`;

const dropdownTemplate = document.createElement('template');
dropdownTemplate.innerHTML = DROPDOWN;

const SEARCH_THRESHOLD = 8;

const enhancedElements = new WeakSet();

class OdSelect extends HTMLElement {
    connectedCallback() {
        if (enhancedElements.has(this)) {
            return;
        }

        this.select = this.querySelector(':scope > select');

        if (this.select === null) {
            throw new Error('od-select renders only through the odSelect macro in templates/elements/form.twig');
        }

        const restored = this.querySelector(':scope > .dropdown') !== null;

        if (restored) {
            this.discardDeadChrome();
        }

        this.multiple      = this.select.multiple;
        this.inlineConfirm = this.hasAttribute('inline-confirm') && !this.multiple;
        this.clearable     = this.hasAttribute('clearable');
        this.icon          = this.getAttribute('icon');
        this.iconSource    = this.getAttribute('icon-src');
        this.pending       = null;
        this.emitting      = false;
        this.searchUrl     = this.getAttribute('search-url');
        this.searchable    = this.searchUrl !== null
            || (this.hasAttribute('searchable') && this.optionCount() >= SEARCH_THRESHOLD);

        this.buildChrome();

        if (restored) {
            this.syncFromSelect();
        }

        this.bindEvents();

        if (this.searchUrl !== null) {
            bindRemoteSearch(this);
        } else if (this.searchable) {
            this.buildFuse();
        }

        enhancedElements.add(this);
    }

    disconnectedCallback() {
        document.removeEventListener('click', this.closeOnOutsideClick);
        window.removeEventListener('resize', this.unlockWidth);
    }

    discardDeadChrome() {
        this.querySelector(':scope > .dropdown').remove();
        this.classList.remove('active', 'confirming', 'keyboard-navigation');
        this.setAttribute('tabindex', '0');
        this.style.width = '';
        this.style.removeProperty('--viewable-option-count');
    }

    optionCount() {
        return Array.from(this.select.options).filter(option => option.value !== '').length;
    }

    buildChrome() {
        this.append(dropdownTemplate.content.cloneNode(true));

        this.dropdown       = this.querySelector('.dropdown');
        this.optionList     = this.querySelector('.options');
        this.selection      = this.querySelector('.selection');
        this.selectionLabel = this.selection.querySelector(':scope > .label');

        if (this.searchable) {
            this.searchInput = this.querySelector('.search input');
            this.searchInput.placeholder = this.getAttribute('search-placeholder') ?? '';
            this.searchInput.addEventListener('change', event => event.stopPropagation());
        } else {
            this.querySelector('.search').remove();
        }

        Array.from(this.select.options).forEach(option => {
            if (option.value === '') {
                return;
            }

            this.optionList.append(this.buildChoice(option));
        });

        this.buildFooter();
    }

    buildChoice(option) {
        const choice = document.createElement('li');
        choice.dataset.value = option.value;

        if (option.disabled) {
            choice.classList.add('disabled');
            choice.setAttribute('aria-disabled', 'true');
        }

        if ('action' in option.dataset) {
            choice.classList.add('action');
        }

        if (this.multiple) {
            const icon = document.createElement('i');
            icon.className = option.selected ? 'fa-solid fa-check' : 'fa-solid fa-plus';
            choice.append(icon);
        }

        choice.append(...[this.buildIcon(option), this.buildLabel(option), this.buildHint(option)].filter(Boolean));

        if (option.selected) {
            choice.classList.add('selected');
        }

        return choice;
    }

    currentOptions() {
        return Array
            .from(this.select.options)
            .filter(option => option.value !== '')
            .map(option => option.cloneNode(true))
        ;
    }

    replaceOptions(options, noResultsLabel = null) {
        const selectedValue = this.select.value;
        const shown         = options.map(option => option.cloneNode(true));

        shown.forEach(option => {
            option.selected = option.value === selectedValue;
        });

        const retained = selectedValue !== '' && !shown.some(option => option.value === selectedValue)
            ? [this.select.selectedOptions[0].cloneNode(true)]
            : []
        ;

        this.select.replaceChildren(...(this.multiple ? [] : [new Option()]), ...retained, ...shown);
        this.optionList.replaceChildren(...shown.map(option => this.buildChoice(option)));

        if (shown.length === 0 && noResultsLabel !== null) {
            const empty = document.createElement('li');
            empty.className = 'no-results disabled';
            empty.setAttribute('aria-disabled', 'true');
            empty.textContent = noResultsLabel;
            this.optionList.append(empty);
        }

        positionDropdown(this);
    }

    buildFooter() {
        const url = this.getAttribute('footer-url');

        if (!url) {
            return;
        }

        const arrow = document.createElement('i');
        arrow.className = 'fa-solid fa-arrow-right';

        const link = document.createElement('a');
        link.className = 'dropdown-footer';
        link.href = url;
        link.setAttribute('hx-boost', 'false');
        link.append(document.createTextNode(this.getAttribute('footer-label') ?? ''), arrow);

        this.querySelector('.dropdown .content').append(link);
    }

    renderSelectionLabel() {
        const option = this.select.selectedOptions[0];

        this.selectionLabel.replaceChildren(...[this.buildIcon(option), this.buildLabel(option)].filter(Boolean));
    }

    buildIcon(option) {
        if (option.dataset.icon) {
            return this.buildIconElement(option.dataset.icon);
        }

        if (option.dataset.iconSrc) {
            return this.buildIconImage(option.dataset.iconSrc);
        }

        return this.buildFieldIcon();
    }

    buildFieldIcon() {
        if (this.icon) {
            return this.buildIconElement(this.icon);
        }

        if (this.iconSource) {
            return this.buildIconImage(this.iconSource);
        }

        return null;
    }

    buildIconElement(classes) {
        const icon = document.createElement('i');
        icon.className = `${classes} select-icon`;

        return icon;
    }

    buildIconImage(source) {
        const image = document.createElement('img');
        image.className = 'select-icon';
        image.src       = source;
        image.alt       = '';

        return image;
    }

    buildLabel(option) {
        if (option.dataset.group) {
            return buildGroupedLabel(option);
        }

        if (!option.dataset.tagColor) {
            const name = document.createElement('span');
            name.className = 'name';
            name.textContent = option.textContent;

            return name;
        }

        const tag = document.createElement('span');
        tag.className = `store-tag store-tag--${option.dataset.tagColor}`;
        tag.textContent = option.textContent;

        return tag;
    }

    buildHint(option) {
        if (!option.dataset.hint) {
            return null;
        }

        const hint = document.createElement('span');
        hint.className = 'option-hint';
        hint.textContent = `(${option.dataset.hint})`;

        return hint;
    }

    buildFuse() {
        const entries = Array
            .from(this.select.options)
            .filter(option => option.value !== '')
            .map(option => ({text: option.textContent, value: option.value}))
        ;

        this.fuse = new Fuse(entries, {
            keys: ['text', 'value'],
            threshold: 0.2,
            findAllMatches: true,
            ignoreLocation: true,
            ignoreDiacritics: true,
        });

        this.searchInput.addEventListener('input', this.filterOptions);
    }

    bindEvents() {
        this.addEventListener('click', this.handleClick);
        this.addEventListener('keydown', this.handleKeydown);
        this.optionList.addEventListener('mousemove', this.stopKeyboardNavigation);
        this.select.addEventListener('change', this.syncFromSelect);
        document.addEventListener('click', this.closeOnOutsideClick);

        if (this.multiple) {
            this.addEventListener('togglealloptions', this.toggleAll);
        }
    }

    open() {
        document.dispatchEvent(new CloseDropdowns());
        document.dispatchEvent(new CloseDropdownMenus());

        this.classList.add('active');
        this.setAttribute('tabindex', '-1');
        positionDropdown(this);

        if (this.searchable) {
            this.searchInput.focus();
        } else {
            this.focus();
        }

        const selected = this.optionList.querySelector('li.selected:not(.hidden)');

        if (selected && outOfVerticalBounds(selected, this.optionList)) {
            const bounds = selected.getBoundingClientRect();

            this.optionList.scrollTop += bounds.top - this.optionList.getBoundingClientRect().top
                - (this.optionList.clientHeight - bounds.height) / 2
            ;
        }
    }

    close() {
        this.clearConfirming();
        this.classList.remove('active', 'keyboard-navigation');
        this.setAttribute('tabindex', '0');
        this.dropdown.classList.remove('inverted');
        this.style.removeProperty('--viewable-option-count');
        this.querySelector('.focus')?.classList.remove('focus');
    }

    chooseOption(choice) {
        if (choice.classList.contains('disabled')) {
            return;
        }

        if (this.inlineConfirm) {
            this.armConfirmation(choice);

            return;
        }

        this.commitOption(choice);
    }

    selectValue(value) {
        const choice = this.optionList.querySelector(`li[data-value="${CSS.escape(value)}"]`);

        if (choice) {
            this.applySelection(choice);
        }
    }

    applySelection(choice) {
        this.querySelector('li.selected')?.classList.remove('selected');
        choice.classList.add('selected');

        this.select.value = choice.dataset.value;
        this.renderSelectionLabel();
        this.classList.add('has-selection');
    }

    commitOption(choice) {
        this.applySelection(choice);
        this.lockWidth();
        this.pulseIcon();

        this.emitChange();

        this.close();
        this.focus();
    }

    clear() {
        this.unlockWidth();

        if (this.multiple) {
            this.optionList
                .querySelectorAll('li.selected')
                .forEach(choice => this.setOptionSelected(choice, false))
            ;
            this.commitSelection();
            this.close();

            return;
        }

        this.querySelector('li.selected')?.classList.remove('selected');
        this.select.value = '';
        this.selectionLabel.replaceChildren();
        this.classList.remove('has-selection');

        this.emitChange();

        this.close();
    }

    armConfirmation(choice) {
        this.clearConfirming();

        this.pending = choice;
        choice.classList.add('pending');
        choice.append(this.buildConfirmActions());
        this.classList.add('confirming');

        positionDropdown(this);
    }

    confirmPending() {
        const choice = this.pending;
        this.clearConfirming();
        this.commitOption(choice);
    }

    lockWidth() {
        if (!this.dataset.confirmAccent) {
            return;
        }

        this.style.width = `${this.offsetWidth}px`;
        window.addEventListener('resize', this.unlockWidth, {once: true});
    }

    unlockWidth = () => {
        this.style.width = '';
    };

    pulseIcon() {
        if (!this.dataset.confirmAccent) {
            return;
        }

        const icon = this.selectionLabel?.querySelector('.select-icon');

        if (!icon) {
            return;
        }

        icon.classList.add('zap');
    }

    cancelPending() {
        if (!this.pending) {
            return;
        }

        this.clearConfirming();
        positionDropdown(this);
        this.focus();
    }

    clearConfirming() {
        this.pending?.querySelector('.confirm-actions')?.remove();
        this.pending?.classList.remove('pending');
        this.pending = null;
        this.classList.remove('confirming');
    }

    buildConfirmActions() {
        const actions = document.createElement('span');
        actions.className = 'confirm-actions';

        const cancelIcon = document.createElement('i');
        cancelIcon.className = 'fa-solid fa-xmark';

        const cancel = document.createElement('button');
        cancel.type = 'button';
        cancel.className = 'text small confirm-no';
        cancel.setAttribute('aria-label', this.getAttribute('cancel-label') ?? '');
        cancel.append(cancelIcon);

        const confirm = document.createElement('button');
        confirm.type = 'button';
        confirm.className = 'success small confirm-yes';
        confirm.textContent = this.getAttribute('confirm-label') ?? '';

        actions.append(cancel, confirm);

        return actions;
    }

    toggleOption(choice) {
        if (choice.classList.contains('disabled')) {
            return;
        }

        this.setOptionSelected(choice, !choice.classList.contains('selected'));
        this.commitSelection();
    }

    addChip(option) {
        const chip = document.createElement('span');
        chip.dataset.value = option.value;

        if (option.dataset.tagColor) {
            chip.classList.add('store-tag', `store-tag--${option.dataset.tagColor}`);
        }

        const remove = document.createElement('i');
        remove.className = 'fa-solid fa-xmark';

        const label = option.dataset.group
            ? buildGroupedLabel(option)
            : document.createTextNode(option.textContent);

        chip.append(...[this.buildIcon(option), label, remove].filter(Boolean));
        this.selection.append(chip);
    }

    setOptionSelected(choice, selected) {
        const option = this.select.querySelector(`option[value="${choice.dataset.value}"]`);

        if (option.disabled || option.selected === selected) {
            return;
        }

        option.selected = selected;
        const icon = choice.querySelector('i');

        if (selected) {
            choice.classList.add('selected');
            icon.classList.replace('fa-plus', 'fa-check');
            this.addChip(option);

            return;
        }

        choice.classList.remove('selected');
        icon.classList.replace('fa-check', 'fa-plus');
        this.selection.querySelector(`span[data-value="${option.value}"]`)?.remove();
    }

    commitSelection() {
        this.classList.toggle('has-selection', this.select.selectedOptions.length > 0);

        this.emitChange();
    }

    emitChange() {
        this.emitting = true;

        document.dispatchEvent(new SelectOptionsChanged(this));
        this.select.dispatchEvent(new Event('input', {bubbles: true}));
        this.select.dispatchEvent(new Event('change', {bubbles: true}));

        this.emitting = false;
    }

    syncFromSelect = () => {
        if (this.emitting) {
            return;
        }

        if (this.multiple) {
            this.selection.querySelectorAll('span[data-value]').forEach(chip => chip.remove());

            Array.from(this.select.options).forEach(option => {
                const choice = this.optionList.querySelector(`li[data-value="${option.value}"]`);

                if (!choice) {
                    return;
                }

                choice.classList.toggle('selected', option.selected);
                choice.querySelector('i').className = option.selected ? 'fa-solid fa-check' : 'fa-solid fa-plus';

                if (option.selected) {
                    this.addChip(option);
                }
            });

            this.classList.toggle('has-selection', this.select.selectedOptions.length > 0);

            return;
        }

        this.querySelector('li.selected')?.classList.remove('selected');

        if (this.select.value === '') {
            this.selectionLabel.replaceChildren();
            this.classList.remove('has-selection');

            return;
        }

        this.optionList.querySelector(`li[data-value="${this.select.value}"]`)?.classList.add('selected');
        this.renderSelectionLabel();
        this.classList.add('has-selection');
    };

    handleClick = event => {
        event.stopPropagation();

        if (this.multiple && event.target.classList.contains('fa-xmark')) {
            const value  = event.target.closest('span').dataset.value;
            const choice = this.optionList.querySelector(`li[data-value="${value}"]`);

            this.setOptionSelected(choice, false);
            this.commitSelection();

            return;
        }

        if (this.clearable && event.target.classList.contains('clear-selection')) {
            event.preventDefault();
            this.clear();
            this.focus();
            this.dispatchEvent(new SelectCleared(this));

            return;
        }

        if (event.target.closest('.toggle-container')) {
            event.preventDefault();
            this.classList.contains('active') ? this.close() : this.open();

            return;
        }

        if (this.pending && event.target.closest('.confirm-actions')) {
            event.preventDefault();
            event.target.closest('.confirm-yes') ? this.confirmPending() : this.cancelPending();

            return;
        }

        const choice = event.target.closest('.dropdown li');

        if (choice) {
            event.preventDefault();
            this.multiple ? this.toggleOption(choice) : this.chooseOption(choice);
        }
    };

    handleKeydown = event => {
        event.stopPropagation();

        if (this.pending) {
            if (event.key === 'Enter') {
                event.preventDefault();
                this.confirmPending();
            } else if (event.key === 'Escape') {
                event.preventDefault();
                this.cancelPending();
            }

            return;
        }

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();

            if (!this.classList.contains('active')) {
                this.open();
            }

            this.classList.add('keyboard-navigation');

            const direction = event.key === 'ArrowUp' ? 'up' : 'down';
            const choice     = navigate(this, direction);

            if (choice && outOfVerticalBounds(choice, this.optionList)) {
                choice.scrollIntoView(direction === 'up');
            }

            return;
        }

        if (event.key === 'Enter' && this.classList.contains('active')) {
            event.preventDefault();

            const choice = this.optionList.querySelector('.focus') || this.optionList.querySelector('li:not(.disabled):hover');

            if (choice) {
                this.multiple ? this.toggleOption(choice) : this.chooseOption(choice);
            }

            return;
        }

        if (event.key === 'Escape' && this.classList.contains('active')) {
            event.preventDefault();
            this.close();
            this.focus();

            return;
        }

        if (event.key === 'Tab' && this.classList.contains('active')) {
            const choice = this.optionList.querySelector('.focus') || this.optionList.querySelector('li:not(.disabled):hover');

            if (choice) {
                event.preventDefault();
                this.chooseOption(choice);

                return;
            }

            this.focus();
            this.close();
        }
    };

    filterOptions = event => {
        this.clearConfirming();

        const choices = Array.from(this.optionList.children);
        this.querySelector('.focus')?.classList.remove('focus');

        if (event.target.value === '') {
            choices.forEach(choice => choice.classList.remove('hidden'));
            choices.find(choice => !choice.classList.contains('disabled'))?.classList.add('focus');

            return;
        }

        const matches = new Set(this.fuse.search(event.target.value).map(result => result.item.value));
        let firstVisible = null;

        choices.forEach(choice => {
            if (!matches.has(choice.dataset.value)) {
                choice.classList.add('hidden');

                return;
            }

            choice.classList.remove('hidden');
            if (!choice.classList.contains('disabled')) {
                firstVisible ??= choice;
            }
        });

        firstVisible?.classList.add('focus');
    };

    stopKeyboardNavigation = () => {
        if (!this.classList.contains('keyboard-navigation')) {
            return;
        }

        this.classList.remove('keyboard-navigation');
        this.querySelector('.focus')?.classList.remove('focus');
    };

    closeOnOutsideClick = event => {
        if (this.classList.contains('active') && !this.contains(event.target)) {
            this.close();
        }
    };

    toggleAll = event => {
        Array.from(this.optionList.children).forEach(choice => this.setOptionSelected(choice, event.checked));
        this.commitSelection();
    };
}

customElements.define('od-select', OdSelect);
